import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF } from "@react-google-maps/api";
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";
import { colors, radius, spacing, typography } from "../../utils/designTokens";
import { getStatusStyle, STATUS_LABELS } from "../../utils/statusColors";
import StatusBadge from "../../components/StatusBadge";
import AppSelect from "../../components/AppSelect";
import { estaBloqueado, fmtFechaLiberacion } from "../../utils/crmAsignacion";
import { FaMapMarkerAlt, FaStar, FaDrawPolygon, FaMapPin, FaLayerGroup, FaLocationArrow } from "react-icons/fa";
import { useIsMobile } from "../../hooks/useResponsive";
import { useProspeccionFilters } from "../../context/ProspeccionFilterContext";
import { useMapaBuscador } from "../../context/MapaBuscadorContext";
import { useToast } from "../../components/Toast";

const CRM_OPTS = [
  { value: "", label: "CRM: cualquiera" },
  ...["sin_contactar", "contactado", "en_conversacion", "reunion", "propuesta", "ganado", "perdido", "descartado"]
    .map((e) => ({ value: e, label: STATUS_LABELS[e] || e })),
];
const REGIMEN_OPTS = [
  { value: "", label: "Todos los regímenes" },
  { value: "ph_en_tramite", label: "PH en trámite" },
  { value: "ph_incorporado", label: "PH incorporado" },
  { value: "comun", label: "Común" },
];
const OBRA_OPTS = [
  { value: "", label: "Obra: cualquiera" },
  { value: "permiso5", label: "Permiso últimos 5 años" },
  { value: "permiso", label: "Con permiso de obra" },
  { value: "sinpermiso", label: "Sin permiso (stock)" },
];
const RADIO_OPTS = [
  { value: 0, label: "Radio: todos" },
  { value: 300, label: "300 m" },
  { value: 500, label: "500 m" },
  { value: 1000, label: "1 km" },
];
const COLOR_OPTS = [
  { value: "score", label: "por score" },
  { value: "crm", label: "por estado CRM" },
];

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const libraries = ["places"]; // igual que MisEdificios → una sola carga del script

const containerStyleBase = { width: "100%", borderRadius: radius.md };
const POCITOS = { lat: -34.9087, lng: -56.1548 };
const CAP_MARKERS = 700;    // tope de marcadores (bajo = mapa ágil; clusteriza igual)
const CAP_PARCELAS = 1200;  // tope de parcelas por viewport (si hay más, "acercá el zoom")

const fmt = (n) => (n == null || n === "" ? "—" : Number(n).toLocaleString("es-UY"));
const currentYear = new Date().getFullYear();

function scoreBucketColor(v) {
  const n = Number(v) || 0;
  if (n >= 80) return colors.success;   // muy caliente
  if (n >= 60) return colors.primary;   // caliente
  if (n >= 40) return colors.warning;   // tibio
  return colors.gray400;                // frío
}
function fechaObraLabel(p) {
  if (p.fechaAperturaObra) return `Obra aprobada ${p.fechaAperturaObra}`;
  if (p.anioObraFallback) return `Última obra (catastro): ${p.anioObraFallback}`;
  return "Sin datos de obra";
}
function anioObra(p) {
  if (p.fechaAperturaObra) return Number(String(p.fechaAperturaObra).slice(0, 4));
  return p.anioObraFallback ? Number(p.anioObraFallback) : null;
}
// Bounding box [minLng, minLat, maxLng, maxLat] de una geometría GeoJSON.
function computeBbox(geom) {
  if (!geom || !geom.coordinates) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const scan = (coords) => {
    for (const c of coords) {
      if (typeof c[0] === "number") {
        if (c[0] < minX) minX = c[0];
        if (c[0] > maxX) maxX = c[0];
        if (c[1] < minY) minY = c[1];
        if (c[1] > maxY) maxY = c[1];
      } else scan(c);
    }
  };
  scan(geom.coordinates);
  return minX === Infinity ? null : [minX, minY, maxX, maxY];
}
function bboxIntersects(b, bounds) {
  const ne = bounds.getNorthEast(), sw = bounds.getSouthWest();
  return !(b[0] > ne.lng() || b[2] < sw.lng() || b[1] > ne.lat() || b[3] < sw.lat());
}
// Debounce simple para no re-renderizar el mapa en cada tecla del filtro.
function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}
// Distancia haversine (metros) entre dos {lat,lng}. Para "cerca mío" del comercial.
function geoDistanceM(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/**
 * Mapa de prospección (Google Maps). Dos vistas:
 *  - Parcelas: dibuja el polígono catastral de cada padrón (capa Data de Google),
 *    coloreado por score/estado CRM, renderizando por viewport (como visor
 *    catastral) — ideal para caminar la zona.
 *  - Puntos: marcadores clusterizados (mejor para la vista macro / mucho zoom out).
 * Filtros compartidos; clic (marcador o parcela) → InfoWindow → ficha CRM.
 */
export default function ProspeccionMapa({ puntos, crmByPadron, onOpenFicha, mapHeight }) {
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries });
  const isMobile = useIsMobile();
  const { registrarBuscador } = useMapaBuscador();
  const { showToast } = useToast();
  const mapRef = useRef(null);
  const [sel, setSel] = useState(null);
  // Pin de una dirección geocodificada que no está mapeada (ofrece "agregar edificio").
  const [busquedaPin, setBusquedaPin] = useState(null);
  // En mobile arranca en "puntos" (marcadores, liviano) — las parcelas son
  // polígonos de la Data Layer de Google, lo más pesado del mapa; el comercial
  // que camina las activa aparte. Desktop arranca en "ambos".
  const [vistaGeo, setVistaGeo] = useState(() => (isMobile ? "puntos" : "ambos")); // ambos | parcelas | puntos
  const [parcelas, setParcelas] = useState([]);
  const [parcelasError, setParcelasError] = useState(null);
  const [mostradas, setMostradas] = useState(0);
  const [demasiadas, setDemasiadas] = useState(0); // #parcelas en vista cuando supera el cap

  // Filtros COMPARTIDOS con la lista y el inicio (contexto). Alias para no tocar
  // el resto del UI. El color, el GPS y el modo de vista son locales del mapa.
  const { filters, setFilter } = useProspeccionFilters();
  const fMinScore = filters.minScore, fEstado = filters.estado, fVis = filters.vis,
    fBarrio = filters.barrio, fSinAdmin = filters.sinAdmin, fObra = filters.obra, fCrm = filters.crm;
  const setFMinScore = (v) => setFilter("minScore", v);
  const setFEstado = (v) => setFilter("estado", v);
  const setFVis = (v) => setFilter("vis", v);
  const setFBarrio = (v) => setFilter("barrio", v);
  const setFSinAdmin = (v) => setFilter("sinAdmin", v);
  const setFObra = (v) => setFilter("obra", v);
  const setFCrm = (v) => setFilter("crm", v);
  const [colorBy, setColorBy] = useState("score"); // score | crm (solo mapa)
  // Ubicación del comercial. El punto azul y el seguimiento son IMPERATIVOS
  // (marker por ref, sin re-render por tick de GPS): así caminar no re-renderiza
  // el mapa cada segundo y el paneo no se atrasa. `hasLoc` solo prende/apaga UI.
  const [hasLoc, setHasLoc] = useState(false);
  const userLocRef = useRef(null);                 // última posición GPS (viva)
  const userMarkerRef = useRef(null);              // punto azul (google.maps.Marker)
  const [fRadio, setFRadio] = useState(0);         // radio en metros; 0 = sin límite
  const [locating, setLocating] = useState(false);
  // Seguimiento GPS continuo: watchPosition mantiene el punto azul en movimiento
  // y el mapa panea siguiendo al comercial mientras camina. Arrastrar el mapa
  // pausa el paneo (el punto sigue vivo); re-tocar "Cerca mío" recentra y retoma.
  const [following, setFollowing] = useState(false); // paneo automático activo
  const watchIdRef = useRef(null);
  const autoPanRef = useRef(false);
  const lastPanRef = useRef(0);
  const firstFixRef = useRef(false);
  // Ubicación "estable" para filtros/orden: se actualiza recién al moverse >60 m,
  // así cada tick del GPS no re-filtra ni re-ordena todos los puntos (jank).
  const [filterLoc, setFilterLoc] = useState(null);
  const filterLocRef = useRef(null);
  // Filtros con debounce para el render pesado del mapa (los controles usan los
  // filtros inmediatos; el mapa se recalcula recién ~180ms después del último cambio).
  const df = useDebounced(filters, 180);

  const conCoords = useMemo(() => puntos.filter((p) => p.lat != null && p.lng != null), [puntos]);
  const puntoByPadron = useMemo(() => {
    const m = {}; for (const p of puntos) m[String(p.padron)] = p; return m;
  }, [puntos]);
  const barrios = useMemo(
    () => [...new Set(conCoords.map((p) => p.barrio).filter(Boolean))].sort(),
    [conCoords]
  );

  const filtrados = useMemo(() => {
    const arr = conCoords.filter((p) => {
      if (Number(p.score || 0) < df.minScore) return false;
      if (df.estado && p.estadoPH !== df.estado) return false;
      if (df.vis && !p.vis) return false;
      if (df.barrio && (p.barrio || "") !== df.barrio) return false;
      if (df.sinAdmin && p.yaAdministrado) return false;
      if (df.obra === "permiso" && !p.tienePermiso) return false;
      if (df.obra === "sinpermiso" && p.tienePermiso) return false;
      if (df.obra === "permiso5") {
        const a = anioObra(p);
        if (!p.tienePermiso || !a || a < currentYear - 5) return false;
      }
      if (df.crm && (crmByPadron[String(p.padron)]?.estado || "sin_contactar") !== df.crm) return false;
      if (filterLoc && fRadio && geoDistanceM(filterLoc, { lat: p.lat, lng: p.lng }) > fRadio) return false;
      return true;
    });
    if (filterLoc) {
      arr.sort((a, b) => geoDistanceM(filterLoc, { lat: a.lat, lng: a.lng }) - geoDistanceM(filterLoc, { lat: b.lat, lng: b.lng }));
    } else {
      arr.sort((a, b) => Number(b.score || 0) - Number(a.score || 0));
    }
    return arr;
  }, [conCoords, df, crmByPadron, filterLoc, fRadio]);

  const filteredPadrones = useMemo(() => new Set(filtrados.map((p) => String(p.padron))), [filtrados]);
  // Conteo de marcadores dibujados en la vista (solo para el texto del contador).
  const [puntosEnVista, setPuntosEnVista] = useState(0);

  // Refs para que los listeners de Google (attachados una vez) vean el estado actual.
  const puntoByPadronRef = useRef(puntoByPadron);
  const crmRef = useRef(crmByPadron);
  const colorByRef = useRef(colorBy);
  const onOpenFichaRef = useRef(onOpenFicha);
  const filteredPadronesRef = useRef(filteredPadrones);
  const vistaGeoRef = useRef(vistaGeo);
  const parcelasRef = useRef(parcelas);
  const shownRef = useRef(new Map()); // padron -> Data.Feature dibujada
  useEffect(() => { puntoByPadronRef.current = puntoByPadron; }, [puntoByPadron]);
  useEffect(() => { crmRef.current = crmByPadron; }, [crmByPadron]);
  useEffect(() => { colorByRef.current = colorBy; }, [colorBy]);
  useEffect(() => { onOpenFichaRef.current = onOpenFicha; }, [onOpenFicha]);
  useEffect(() => { filteredPadronesRef.current = filteredPadrones; }, [filteredPadrones]);
  useEffect(() => { vistaGeoRef.current = vistaGeo; }, [vistaGeo]);
  useEffect(() => { parcelasRef.current = parcelas; }, [parcelas]);

  // Cargar los polígonos de parcela (GeoJSON chunkeado) una vez.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "prospeccion_parcelas"));
        const feats = [];
        snap.forEach((d) => {
          let arr; try { arr = JSON.parse(d.data().features || "[]"); } catch (_) { return; }
          for (const f of arr) {
            const bbox = computeBbox(f.geometry);
            if (bbox) feats.push({ padron: f.properties.padron, geometry: f.geometry, bbox });
          }
        });
        if (!cancel) { setParcelas(feats); setParcelasError(feats.length ? null : "vacío"); }
      } catch (e) {
        if (!cancel) setParcelasError(e?.code === "permission-denied"
          ? "Sin permiso para leer las parcelas — falta publicar la regla de prospeccion_parcelas."
          : (e?.message || "No se pudieron cargar las parcelas."));
      }
    })();
    return () => { cancel = true; };
  }, []);

  const colorForPunto = useCallback((p) => {
    if (!p) return colors.gray300;
    if (colorByRef.current === "crm") return getStatusStyle(crmRef.current[String(p.padron)]?.estado || "sin_contactar").text;
    return scoreBucketColor(p.score);
  }, []);

  // Estilo de cada parcela (lee refs → siempre actualizado).
  const styleFeature = useCallback((feature) => {
    const padron = String(feature.getProperty("padron"));
    const p = puntoByPadronRef.current[padron];
    return { fillColor: colorForPunto(p), fillOpacity: 0.5, strokeColor: "#ffffff", strokeWeight: 0.8, clickable: true };
  }, [colorForPunto]);

  // Redibuja las parcelas del viewport actual (diff incremental).
  const renderParcelas = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const shown = shownRef.current;
    if (vistaGeoRef.current === "puntos") {
      shown.forEach((f) => map.data.remove(f)); shown.clear();
      setMostradas(0); setDemasiadas(0); return;
    }
    const bounds = map.getBounds();
    if (!bounds) return;
    const fp = filteredPadronesRef.current;
    let count = 0;
    const target = [];
    for (const par of parcelasRef.current) {
      if (!fp.has(String(par.padron)) || !bboxIntersects(par.bbox, bounds)) continue;
      count++;
      if (count <= CAP_PARCELAS) target.push(par);
    }
    if (count > CAP_PARCELAS) {
      shown.forEach((f) => map.data.remove(f)); shown.clear();
      setMostradas(0); setDemasiadas(count); return;
    }
    setDemasiadas(0);
    const targetSet = new Set(target.map((t) => String(t.padron)));
    shown.forEach((f, padron) => { if (!targetSet.has(padron)) { map.data.remove(f); shown.delete(padron); } });
    for (const par of target) {
      const key = String(par.padron);
      if (shown.has(key)) continue;
      const added = map.data.addGeoJson({ type: "Feature", geometry: par.geometry, properties: { padron: par.padron } });
      if (added && added[0]) shown.set(key, added[0]);
    }
    setMostradas(shown.size);
  }, []);
  const renderRef = useRef(renderParcelas);
  useEffect(() => { renderRef.current = renderParcelas; }, [renderParcelas]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    map.data.setStyle(styleFeature);
    map.data.addListener("click", (e) => {
      const padron = String(e.feature.getProperty("padron"));
      const p = puntoByPadronRef.current[padron];
      if (p) setSel(p);
    });
    map.data.addListener("mouseover", () => map.setOptions({ draggableCursor: "pointer" }));
    map.data.addListener("mouseout", () => map.setOptions({ draggableCursor: null }));
    map.addListener("idle", () => {
      renderRef.current && renderRef.current();          // parcelas del viewport
      renderPuntosRef.current && renderPuntosRef.current(); // marcadores del viewport
    });
    // Arrastrar el mapa pausa el seguimiento (el GPS sigue vivo, solo deja de panear).
    map.addListener("dragstart", () => {
      if (autoPanRef.current) { autoPanRef.current = false; setFollowing(false); }
    });
  }, [styleFeature]);

  // Redibuja al cambiar filtros / vista / parcelas cargadas.
  useEffect(() => {
    if (isLoaded && mapRef.current) renderRef.current();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [vistaGeo, filteredPadrones, parcelas, isLoaded]);

  // Recolorea al cambiar el criterio de color o el CRM.
  useEffect(() => {
    if (isLoaded && mapRef.current) mapRef.current.data.setStyle(styleFeature);
  }, [colorBy, crmByPadron, puntoByPadron, isLoaded, styleFeature]);

  // Apagar el GPS y quitar el punto azul al salir de la pantalla.
  useEffect(() => () => {
    if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    if (userMarkerRef.current) { userMarkerRef.current.setMap(null); userMarkerRef.current = null; }
  }, []);

  // Capa de marcadores IMPERATIVA con diff incremental (mismo patrón que las
  // parcelas): en cada "idle" se dibujan solo los puntos del viewport (con 25%
  // de margen para que paneos chicos no redibujen) y se remueven los que salieron.
  // Nada de re-crear cientos de MarkerF de React en cada movimiento — eso
  // trancaba el mapa cuando la colección creció a ~11.7k puntos.
  const markersRef = useRef(new Map()); // padron -> google.maps.Marker
  // Menos marcadores en mobile = mapa más ágil al caminar (igual clusteriza por cap).
  const capMarkersRef = useRef(isMobile ? 400 : CAP_MARKERS);
  useEffect(() => { capMarkersRef.current = isMobile ? 400 : CAP_MARKERS; }, [isMobile]);
  const iconForPunto = useCallback((p) => {
    const color = colorByRef.current === "crm"
      ? getStatusStyle(crmRef.current[String(p.padron)]?.estado || "sin_contactar").text
      : scoreBucketColor(p.score);
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: color, fillOpacity: 0.9,
      scale: p.yaAdministrado ? 5 : 6.5, strokeColor: "#ffffff", strokeWeight: 1.2,
    };
  }, []);
  const filtradosRef = useRef(filtrados);
  useEffect(() => { filtradosRef.current = filtrados; }, [filtrados]);

  const renderPuntos = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const shown = markersRef.current;
    if (vistaGeoRef.current === "parcelas") {
      shown.forEach((m) => m.setMap(null)); shown.clear(); setPuntosEnVista(0); return;
    }
    const b = map.getBounds();
    if (!b) return;
    const ne = b.getNorthEast(), sw = b.getSouthWest();
    const dLat = (ne.lat() - sw.lat()) * 0.25, dLng = (ne.lng() - sw.lng()) * 0.25;
    const n = ne.lat() + dLat, s = sw.lat() - dLat, e = ne.lng() + dLng, w = sw.lng() - dLng;
    // filtradosRef ya viene ordenado por score desc → el cap se queda con los mejores.
    const target = [];
    const cap = capMarkersRef.current;
    for (const p of filtradosRef.current) {
      if (p.lat <= n && p.lat >= s && p.lng <= e && p.lng >= w) {
        target.push(p);
        if (target.length >= cap) break;
      }
    }
    const targetIds = new Set(target.map((p) => String(p.padron)));
    shown.forEach((m, k) => { if (!targetIds.has(k)) { m.setMap(null); shown.delete(k); } });
    for (const p of target) {
      const key = String(p.padron);
      if (shown.has(key)) continue;
      const m = new window.google.maps.Marker({
        map, position: { lat: p.lat, lng: p.lng }, icon: iconForPunto(p),
        title: p.direccion || `Padrón ${p.padron}`,
      });
      m.addListener("click", () => setSel(puntoByPadronRef.current[key] || p));
      shown.set(key, m);
    }
    setPuntosEnVista(shown.size);
  }, [iconForPunto]);
  const renderPuntosRef = useRef(renderPuntos);
  useEffect(() => { renderPuntosRef.current = renderPuntos; }, [renderPuntos]);

  // Redibuja marcadores al cambiar filtros/vista; recolorea al cambiar criterio o CRM.
  useEffect(() => {
    if (isLoaded && mapRef.current) renderPuntosRef.current();
  }, [vistaGeo, filtrados, isLoaded]);
  useEffect(() => {
    markersRef.current.forEach((m, k) => {
      const p = puntoByPadronRef.current[k];
      if (p) m.setIcon(iconForPunto(p));
    });
  }, [colorBy, crmByPadron, iconForPunto]);
  // Limpieza al desmontar (los Marker imperativos no los limpia React).
  useEffect(() => () => { markersRef.current.forEach((m) => m.setMap(null)); markersRef.current.clear(); }, []);

  // ── Buscador de direcciones (input en el HeaderComercial vía context) ──
  // 1) padrón numérico o dirección que YA está en el mapa → centra y abre su ficha.
  // 2) dirección no mapeada → geocodifica con Google, centra, deja un pin y ofrece
  //    "Agregar edificio" (alta manual: abre la ficha CRM precargada con coords).
  const conCoordsRef = useRef(conCoords);
  useEffect(() => { conCoordsRef.current = conCoords; }, [conCoords]);

  const centrarEn = useCallback((lat, lng, zoom = 17) => {
    const map = mapRef.current;
    if (!map) return;
    map.panTo({ lat, lng });
    if (map.getZoom() < zoom) map.setZoom(zoom);
  }, []);

  const handleBuscar = useCallback(async (entrada) => {
    // Lugar ya resuelto por el autocompletado de Google → pin + alta directo.
    if (entrada && typeof entrada === "object" && entrada.lat != null && entrada.lng != null) {
      setSel(null);
      setBusquedaPin({ lat: entrada.lat, lng: entrada.lng, direccion: entrada.direccion || "Ubicación elegida" });
      centrarEn(entrada.lat, entrada.lng, 18);
      return;
    }
    const q = String(entrada || "").trim();
    if (!q) return;
    const pts = conCoordsRef.current;
    // 1a) padrón exacto
    if (/^\d+$/.test(q)) {
      const p = pts.find((x) => String(x.padron) === q);
      if (p) { setBusquedaPin(null); centrarEn(p.lat, p.lng); setSel(p); return; }
    }
    // 1b) dirección que matchea un punto cargado
    const qLow = q.toLowerCase();
    const match = pts.find((x) => (x.direccion || "").toLowerCase().includes(qLow));
    if (match) { setBusquedaPin(null); centrarEn(match.lat, match.lng); setSel(match); return; }

    // 2) geocodificar con Google (ya cargado) y ofrecer alta manual
    if (!window.google || !window.google.maps) { showToast("El mapa aún se está cargando.", "error"); return; }
    try {
      const geocoder = new window.google.maps.Geocoder();
      const { results } = await geocoder.geocode({
        address: /montevideo|uruguay|canelones/i.test(q) ? q : `${q}, Montevideo, Uruguay`,
        componentRestrictions: { country: "UY" },
      });
      const r = results && results[0];
      if (!r) { showToast("No se encontró esa dirección.", "error"); return; }
      const lat = r.geometry.location.lat(), lng = r.geometry.location.lng();
      setSel(null);
      setBusquedaPin({ lat, lng, direccion: r.formatted_address.replace(/, Uruguay$/, "") });
      centrarEn(lat, lng, 18);
    } catch (e) {
      showToast("No se encontró esa dirección.", "error");
    }
  }, [centrarEn, showToast]);

  // Registrar/quitar el handler en el context según el mapa esté montado.
  const handleBuscarRef = useRef(handleBuscar);
  useEffect(() => { handleBuscarRef.current = handleBuscar; }, [handleBuscar]);
  useEffect(() => {
    registrarBuscador((t) => handleBuscarRef.current(t));
    return () => registrarBuscador(null);
  }, [registrarBuscador]);

  // Abre la ficha CRM para dar de alta el edificio geocodificado (id sintético
  // "M-<ts>"): al guardar queda en prospectos_crm con sus coordenadas y el mapa
  // lo muestra como punto manual.
  const agregarManual = useCallback(() => {
    if (!busquedaPin) return;
    const id = `M-${Date.now()}`;
    onOpenFichaRef.current && onOpenFichaRef.current({
      padron: id, lat: busquedaPin.lat, lng: busquedaPin.lng,
      direccion: busquedaPin.direccion, manual: true,
      score: 50, estadoPH: "comun", nUnidades: null,
    });
    setBusquedaPin(null);
  }, [busquedaPin]);

  // Opciones del mapa estables (un objeto nuevo por render dispara setOptions).
  const mapOptions = useMemo(
    () => ({ streetViewControl: false, mapTypeControl: !isMobile, fullscreenControl: true, gestureHandling: "greedy" }),
    [isMobile]
  );
  const mapContainerStyle = useMemo(
    () => ({ ...containerStyleBase, height: mapHeight || (isMobile ? "68vh" : "640px") }),
    [mapHeight, isMobile]
  );

  if (!GOOGLE_MAPS_API_KEY) {
    return <div style={warnBox}>Falta configurar <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> para ver el mapa.</div>;
  }
  if (loadError) return <div style={warnBox}>No se pudo cargar Google Maps.</div>;

  // "Cerca mío": arranca un watchPosition que sigue al comercial mientras camina.
  // Primer toque: ubica, acerca el zoom y activa el seguimiento. Si el usuario
  // arrastró el mapa (pausa), volver a tocar recentra y retoma el paneo.
  const locateMe = () => {
    if (!navigator.geolocation) return;
    autoPanRef.current = true;
    setFollowing(true);
    if (watchIdRef.current != null) {
      // Watch ya activo: recentrar de una con la última posición conocida.
      if (userLocRef.current && mapRef.current) {
        mapRef.current.panTo(userLocRef.current);
        if (mapRef.current.getZoom() < 16) mapRef.current.setZoom(17);
      }
      return;
    }
    setLocating(true);
    firstFixRef.current = false;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        userLocRef.current = loc;
        setLocating(false);   // no-op tras el 1er fix (React descarta el mismo valor)
        setHasLoc(true);

        // Punto azul IMPERATIVO: se mueve con cada fix sin re-renderizar el mapa.
        const map = mapRef.current;
        if (map && window.google) {
          if (!userMarkerRef.current) {
            userMarkerRef.current = new window.google.maps.Marker({
              map, position: loc, zIndex: 9999, clickable: false, title: "Mi ubicación",
              icon: { path: window.google.maps.SymbolPath.CIRCLE, fillColor: "#1d4ed8", fillOpacity: 1, scale: 7, strokeColor: "#ffffff", strokeWeight: 3 },
            });
          } else {
            userMarkerRef.current.setPosition(loc);
          }
        }

        // Ubicación estable para filtros/orden: recién al moverse >60 m (evita re-filtrar cada tick).
        const fl = filterLocRef.current;
        if (!fl || geoDistanceM(fl, loc) > 60) {
          filterLocRef.current = loc;
          setFilterLoc(loc);
        }
        // Seguir al comercial: paneo suave con throttle (~1/s), siempre a la posición más fresca.
        const now = Date.now();
        if (autoPanRef.current && map && now - lastPanRef.current > 900) {
          lastPanRef.current = now;
          map.panTo(loc);
          if (!firstFixRef.current) {
            firstFixRef.current = true;
            if (map.getZoom() < 16) map.setZoom(17);
          }
        }
      },
      (err) => {
        setLocating(false);
        // Solo DETENER el seguimiento si el usuario negó el permiso (PERMISSION_DENIED).
        // Un error transitorio (timeout / señal perdida en un túnel o entre edificios)
        // NO debe matar el watch: watchPosition sigue reintentando y se recupera solo
        // cuando vuelve el GPS. Antes lo limpiábamos acá y la ubicación quedaba congelada.
        if (err && err.code === 1 /* PERMISSION_DENIED */) {
          autoPanRef.current = false;
          setFollowing(false);
          if (watchIdRef.current != null) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
        }
        // POSITION_UNAVAILABLE (2) / TIMEOUT (3): transitorio → se ignora, el watch sigue vivo.
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 1000 }
    );
  };

  return (
    <div>
      {/* Filtros — mobile: 2 líneas con controles proporcionados; desktop: barra completa */}
      {isMobile ? (
        // Filtros compactos en 2 filas para que el mapa ocupe más pantalla:
        // capa (Puntos / + Parcelas) + fila de CRM · Cerca mío · Radio.
        <div style={{ marginBottom: spacing[2] }}>
          <div style={{ display: "flex", gap: 6, marginBottom: spacing[2] }}>
            <button onClick={() => setVistaGeo("puntos")} style={{ ...segBtn(vistaGeo === "puntos"), flex: 1, justifyContent: "center" }}><FaMapPin size={11} /> Puntos</button>
            <button onClick={() => setVistaGeo("ambos")} style={{ ...segBtn(vistaGeo === "ambos"), flex: 1, justifyContent: "center" }}><FaLayerGroup size={11} /> + Parcelas</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: hasLoc ? "1fr 1fr 1fr" : "1fr 1fr", gap: spacing[2] }}>
            <AppSelect value={fCrm} onChange={setFCrm} options={CRM_OPTS} ariaLabel="Estado CRM" />
            <button
              onClick={locateMe}
              disabled={locating}
              style={{ ...mBtn, ...(following ? { borderColor: colors.primary, background: colors.primaryLighter, color: colors.primary } : hasLoc ? { borderColor: colors.primary, color: colors.primary } : {}) }}
              aria-label="Seguir mi ubicación"
            >
              <FaLocationArrow size={11} /> {locating ? "Ubicando…" : following ? "Siguiendo" : hasLoc ? "Recentrar" : "Cerca mío"}
            </button>
            {hasLoc && (
              <AppSelect value={fRadio} onChange={(v) => setFRadio(Number(v))} ariaLabel="Radio"
                options={[{ value: 0, label: "Radio" }, { value: 300, label: "300 m" }, { value: 500, label: "500 m" }, { value: 1000, label: "1 km" }]} />
            )}
          </div>
        </div>
      ) : (
        <div style={filtersRow}>
          <div style={segmented}>
            <button onClick={() => setVistaGeo("ambos")} style={segBtn(vistaGeo === "ambos")}><FaLayerGroup size={12} /> Ambos</button>
            <button onClick={() => setVistaGeo("parcelas")} style={segBtn(vistaGeo === "parcelas")}><FaDrawPolygon size={12} /> Parcelas</button>
            <button onClick={() => setVistaGeo("puntos")} style={segBtn(vistaGeo === "puntos")}><FaMapPin size={12} /> Puntos</button>
          </div>
          <label style={ctrlLabel}>Score ≥
            <input type="number" min={0} max={100} value={fMinScore} onChange={(e) => setFMinScore(Number(e.target.value) || 0)} style={{ ...num, marginLeft: 6 }} />
          </label>
          <div style={{ width: 170 }}>
            <AppSelect value={fEstado} onChange={setFEstado} options={REGIMEN_OPTS} ariaLabel="Régimen" />
          </div>
          <div style={{ width: 170 }}>
            <AppSelect value={fBarrio} onChange={setFBarrio} ariaLabel="Barrio"
              options={[{ value: "", label: "Todos los barrios" }, ...barrios.map((b) => ({ value: b, label: b }))]} />
          </div>
          <div style={{ width: 190 }}>
            <AppSelect value={fObra} onChange={setFObra} options={OBRA_OPTS} ariaLabel="Obra" />
          </div>
          <div style={{ width: 170 }}>
            <AppSelect value={fCrm} onChange={setFCrm} options={CRM_OPTS} ariaLabel="Estado CRM" />
          </div>
          <label style={ctrlLabel}><input type="checkbox" checked={fVis} onChange={(e) => setFVis(e.target.checked)} /> Vivienda Promovida</label>
          <label style={ctrlLabel}><input type="checkbox" checked={fSinAdmin} onChange={(e) => setFSinAdmin(e.target.checked)} /> Excluir administrados</label>
          <button onClick={locateMe} disabled={locating} style={{ ...segBtn(following || hasLoc), border: `1px solid ${hasLoc ? colors.primary : colors.gray200}`, borderRadius: radius.sm }} aria-label="Seguir mi ubicación">
            <FaLocationArrow size={11} /> {locating ? "Ubicando…" : following ? "Siguiendo" : hasLoc ? "Recentrar" : "Cerca mío"}
          </button>
          {hasLoc && (
            <div style={{ width: 130 }}>
              <AppSelect value={fRadio} onChange={(v) => setFRadio(Number(v))} options={RADIO_OPTS} ariaLabel="Radio" />
            </div>
          )}
          <div style={{ flex: 1 }} />
          <label style={{ ...ctrlLabel, gap: 6 }}>Color:
            <div style={{ width: 150 }}>
              <AppSelect value={colorBy} onChange={setColorBy} options={COLOR_OPTS} ariaLabel="Colorear por" />
            </div>
          </label>
        </div>
      )}

      {/* Leyenda + conteo (leyenda de colores solo en desktop, para ganar espacio en mobile) */}
      <div style={legendRow}>
        {!isMobile && (colorBy === "score" ? (
          <>
            <Legend color={colors.success} text="≥ 80 muy caliente" />
            <Legend color={colors.primary} text="60–79 caliente" />
            <Legend color={colors.warning} text="40–59 tibio" />
            <Legend color={colors.gray400} text="< 40 frío" />
          </>
        ) : (
          ["sin_contactar", "contactado", "en_conversacion", "reunion", "propuesta", "ganado", "perdido"]
            .map((e) => <Legend key={e} color={getStatusStyle(e).text} text={STATUS_LABELS[e]} />)
        ))}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: typography.sizes.sm, color: (vistaGeo !== "puntos" && parcelasError) ? colors.error : colors.gray600 }}>
          {vistaGeo !== "puntos"
            ? (parcelasError
                ? `⚠️ ${parcelasError}`
                : demasiadas > 0
                  ? `Hay ${fmt(demasiadas)} parcelas en esta vista — acercá el zoom para dibujarlas`
                  : `${fmt(mostradas)} parcelas · ${fmt(filteredPadrones.size)} en total`)
            : `${fmt(puntosEnVista)} puntos en vista · ${fmt(filtrados.length)} en total`}
        </span>
      </div>

      {!isLoaded ? (
        <div style={{ ...warnBox, textAlign: "center" }}>Cargando mapa…</div>
      ) : (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={POCITOS}
          zoom={14}
          onLoad={onMapLoad}
          options={mapOptions}
        >
          {/* El punto azul de "mi ubicación" es imperativo (userMarkerRef) para no
              re-renderizar en cada tick de GPS — ver locateMe(). */}

          {/* Dirección buscada que no está mapeada → pin + alta manual.
              El InfoWindow lleva `position` propia (no anclado al marker): anclarlo
              al MarkerF tira "Invariant Violation: must provide an anchor". */}
          {busquedaPin && (
            <>
              <MarkerF
                position={{ lat: busquedaPin.lat, lng: busquedaPin.lng }}
                icon={{
                  path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                  fillColor: "#0080ff", fillOpacity: 1, scale: 6, strokeColor: "#ffffff", strokeWeight: 1.5,
                }}
                zIndex={9998}
              />
              <InfoWindowF
                position={{ lat: busquedaPin.lat, lng: busquedaPin.lng }}
                onCloseClick={() => setBusquedaPin(null)}
              >
                <div style={{ minWidth: 200, fontFamily: typography.fontFamily }}>
                  <div style={{ fontSize: 13, color: colors.gray900, marginBottom: 2 }}>{busquedaPin.direccion}</div>
                  <div style={{ fontSize: 12, color: colors.gray600, marginBottom: 8 }}>Este edificio no está en el mapa.</div>
                  <button onClick={agregarManual} style={infoBtn}>+ Agregar edificio acá</button>
                </div>
              </InfoWindowF>
            </>
          )}

          {sel && (
            <InfoWindowF position={{ lat: sel.lat, lng: sel.lng }} onCloseClick={() => setSel(null)}>
              <div style={{ minWidth: 220, fontFamily: typography.fontFamily }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <strong style={{ fontSize: 14, fontWeight: 400, color: colors.gray900 }}>{sel.direccion || `Padrón ${sel.padron}`}</strong>
                  <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 3, color: scoreBucketColor(sel.score) }}>
                    <FaStar size={11} /> {sel.score}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                  <StatusBadge status={sel.estadoPH} size="sm" />
                  {sel.vis && <StatusBadge status="vivienda_promovida" label="VP" size="sm" />}
                  {crmByPadron[String(sel.padron)]?.estado && (
                    <StatusBadge status={crmByPadron[String(sel.padron)].estado} size="sm" />
                  )}
                </div>
                <div style={{ fontSize: 12, color: colors.gray600, lineHeight: 1.5 }}>
                  <div>Padrón {sel.padron}{sel.barrio ? ` · ${sel.barrio}` : ""}</div>
                  {crmByPadron[String(sel.padron)]?.asignadoA && (
                    estaBloqueado(crmByPadron[String(sel.padron)]) ? (
                      <div style={{ color: colors.primary }}>👤 {crmByPadron[String(sel.padron)].asignadoA} · 🔒 se libera {fmtFechaLiberacion(crmByPadron[String(sel.padron)])}</div>
                    ) : (
                      <div style={{ color: colors.gray600 }}>👤 {crmByPadron[String(sel.padron)].asignadoA} · libre (venció)</div>
                    )
                  )}
                  {filterLoc && <div style={{ color: colors.primary }}>📍 a {fmt(Math.round(geoDistanceM(filterLoc, { lat: sel.lat, lng: sel.lng })))} m tuyo</div>}
                  <div>{fechaObraLabel(sel)}</div>
                  {sel.valorCatastral != null && <div>Valor catastral: $ {fmt(sel.valorCatastral)}</div>}
                  <div>{fmt(sel.totalM2 || sel.areaEdif)} m² · {fmt(sel.nUnidades)} unid. · año {sel.anioConstruccion || "?"}</div>
                  {sel.empresa && <div>Desarrollador: {sel.empresa}</div>}
                </div>
                <button onClick={() => { onOpenFicha(sel); setSel(null); }} style={infoBtn}>
                  Abrir ficha CRM →
                </button>
              </div>
            </InfoWindowF>
          )}
        </GoogleMap>
      )}
      {!isMobile && (
        <div style={{ marginTop: spacing[2], fontSize: typography.sizes.xs, color: colors.gray400 }}>
          <FaMapMarkerAlt size={10} /> Fuente: Intendencia de Montevideo (datos abiertos) + relevamiento catastral propio.
          {vistaGeo !== "puntos"
            ? " Parcelas coloreadas por score + puntos para leer la zona. Clic en una parcela o punto → ficha."
            : " Círculos chicos = padrones ya administrados."}
        </div>
      )}
    </div>
  );
}

function Legend({ color, text }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: typography.sizes.xs, color: colors.gray600 }}>
      <span style={{ width: 10, height: 10, borderRadius: 2, background: color, display: "inline-block", opacity: 0.7 }} /> {text}
    </span>
  );
}

const filtersRow = { display: "flex", gap: spacing[2], alignItems: "center", flexWrap: "wrap", marginBottom: spacing[3] };
const legendRow = { display: "flex", gap: spacing[3], alignItems: "center", flexWrap: "wrap", marginBottom: spacing[2] };
const ctrlLabel = { display: "flex", alignItems: "center", gap: spacing[1], fontSize: typography.sizes.sm, color: colors.gray700 };
const num = { width: 64, padding: spacing[1], border: `1px solid ${colors.gray200}`, borderRadius: radius.sm };
const segmented = { display: "inline-flex", border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, overflow: "hidden" };
// Controles mobile del mapa: misma altura (42px), full-width dentro del grid.
const mBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, height: 42, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, background: colors.white, color: colors.gray700, cursor: "pointer", fontSize: typography.sizes.base, fontFamily: "inherit" };
const segBtn = (active) => ({
  display: "inline-flex", alignItems: "center", gap: spacing[1], padding: `${spacing[1]}px ${spacing[3]}px`,
  background: active ? colors.primary : colors.white, color: active ? "#fff" : colors.gray700,
  border: "none", cursor: "pointer", fontSize: typography.sizes.sm,
});
const warnBox = {
  padding: spacing[6], background: colors.gray50, border: `1px solid ${colors.gray200}`,
  borderRadius: radius.md, color: colors.gray600, fontSize: typography.sizes.base,
};
const infoBtn = {
  marginTop: 8, width: "100%", padding: "6px 10px", background: colors.primary, color: "#fff",
  border: "none", borderRadius: radius.sm, cursor: "pointer", fontSize: 12,
};
